import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

import { Component, ErrorInfo, ReactNode } from "react";

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
    constructor(props: { children: ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: "2rem", fontFamily: "sans-serif", maxWidth: "800px", margin: "0 auto" }}>
                    <h1 style={{ fontSize: "2rem", color: "#e11d48", marginBottom: "1rem" }}>Something went wrong</h1>
                    <p style={{ marginBottom: "1.5rem", lineHeight: "1.6" }}>
                        The application encountered an unexpected error properly rendering.
                        This is often caused by missing configuration.
                    </p>
                    <div style={{ background: "#f1f5f9", padding: "1.5rem", borderRadius: "0.5rem", overflowX: "auto" }}>
                        <p style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>Error Details:</p>
                        <pre style={{ fontSize: "0.875rem", color: "#334155" }}>
                            {this.state.error?.message}
                        </pre>
                        {this.state.error?.message?.includes("Supabase") && (
                            <div style={{ marginTop: "1rem", color: "#0f766e", borderTop: "1px solid #cbd5e1", paddingTop: "1rem" }}>
                                <strong>Tip:</strong> It looks like a Supabase configuration issue.
                                Please check that <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_PUBLISHABLE_KEY</code>
                                are correctly set in your environment variables (e.g., Vercel Project Settings).
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: "2rem",
                            padding: "0.75rem 1.5rem",
                            background: "#0f172a",
                            color: "white",
                            border: "none",
                            borderRadius: "0.375rem",
                            cursor: "pointer"
                        }}
                    >
                        Reload Application
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

createRoot(document.getElementById("root")!).render(
    <ErrorBoundary>
        <App />
    </ErrorBoundary>
);
